import { Injectable } from '@angular/core';

/**
 * Servicio para descifrar configuraciones cifradas utilizando AES-GCM y PBKDF2.
 *
 * Este servicio proporciona métodos para:
 * - Convertir cadenas en base64url a ArrayBuffer.
 * - Parsear valores primitivos desde cadenas.
 * - Derivar claves criptográficas a partir de una contraseña y una sal.
 * - Descifrar valores cifrados.
 * - Recorrer y descifrar recursivamente objetos o arreglos cifrados.
 * - Reconstruir objetos a partir de entradas clave-valor.
 * - Descifrar una configuración completa usando una contraseña.
 *
 * @example
 * ```typescript
 * const configDescifrada = await decryptConfigService.decryptConfig(configCifrada, 'contraseña');
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class DecryptConfigService {
  /**
   * Convierte una cadena en formato base64url a un ArrayBuffer.
   *
   * Esta función reemplaza los caracteres específicos de base64url por los equivalentes de base64 estándar,
   * añade el relleno necesario y decodifica la cadena resultante a un ArrayBuffer.
   *
   * @param b64url - Cadena en formato base64url a convertir.
   * @returns Un ArrayBuffer que representa los datos decodificados.
   * @throws Error si la cadena base64url tiene un formato inválido.
   */
  private base64urlToArrayBuffer(b64url: string): ArrayBuffer {
    let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad === 2) b64 += '==';
    else if (pad === 3) b64 += '=';
    else if (pad !== 0) throw new Error('Base64url inválido');

    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Intenta convertir una cadena de texto en un valor primitivo.
   *
   * Si el valor es "true" o "false" (ignorando mayúsculas/minúsculas), retorna el valor booleano correspondiente.
   * Si el valor representa un número (entero o decimal), retorna el valor numérico.
   * En cualquier otro caso, retorna la cadena original.
   *
   * @param value La cadena de texto a convertir.
   * @returns El valor convertido como string, number o boolean.
   */
  private parsePrimitive(value: string): string | number | boolean {
    const low = value.toLowerCase();
    if (low === 'true') return true;
    if (low === 'false') return false;
    if (!isNaN(value as any)) return value.includes('.') ? parseFloat(value) : parseInt(value, 10);
    return value;
  }

  /**
   * Deriva una clave criptográfica AES-GCM de 256 bits a partir de una contraseña y una sal utilizando PBKDF2.
   *
   * @param password - La contraseña en texto plano que se utilizará como base para la derivación de la clave.
   * @param salt - Un ArrayBuffer que representa la sal criptográfica utilizada para fortalecer la derivación.
   * @returns Una promesa que resuelve en un objeto `CryptoKey` listo para operaciones de descifrado.
   *
   * @remarks
   * - Utiliza 100,000 iteraciones y SHA-256 como función hash para PBKDF2.
   * - La clave derivada solo tiene permiso para operaciones de descifrado (`['decrypt']`).
   * - Este método es asíncrono y depende de la API Web Crypto (`window.crypto.subtle`).
   */
  private async deriveKey(password: string, salt: ArrayBuffer): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: 100_000,
        hash: 'SHA-256',
      },
      keyMaterial,
      {
        name: 'AES-GCM',
        length: 256,
      },
      false,
      ['decrypt']
    );
  }

  /**
   * Descifra una cadena cifrada utilizando el algoritmo AES-GCM.
   *
   * @param encStr - Cadena cifrada en formato base64url.
   * @param key - Clave criptográfica (`CryptoKey`) utilizada para el descifrado.
   * @returns Una promesa que resuelve con la cadena descifrada en texto plano.
   *
   * @throws Si ocurre un error durante el proceso de descifrado.
   */
  private async decryptValue(encStr: string, key: CryptoKey): Promise<string> {
    const data = new Uint8Array(this.base64urlToArrayBuffer(encStr));
    const nonce = data.slice(0, 12);
    const ciphertext = data.slice(12);

    const plainBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: nonce,
      },
      key,
      ciphertext
    );

    return new TextDecoder().decode(plainBuffer);
  }

  /**
   * Recorre recursivamente un objeto, arreglo o valor primitivo y desencripta todos los valores de tipo string utilizando la clave proporcionada.
   *
   * - Si el valor es un arreglo, desencripta cada elemento recursivamente.
   * - Si el valor es un objeto, desencripta recursivamente cada propiedad.
   * - Si el valor es un string, intenta desencriptarlo y convertirlo a su tipo primitivo original.
   * - Si el valor no es un objeto, arreglo o string, lo retorna tal cual.
   *
   * @param obj El objeto, arreglo o valor primitivo a desencriptar.
   * @param key La clave criptográfica utilizada para desencriptar los valores.
   * @returns Una promesa que resuelve al objeto, arreglo o valor desencriptado.
   */
  private async recurseDecrypt(obj: any, key: CryptoKey): Promise<any> {
    if (Array.isArray(obj)) {
      return Promise.all(obj.map(item => this.recurseDecrypt(item, key)));
    }

    if (typeof obj === 'object' && obj !== null) {
      const entries = await Promise.all(
        Object.entries(obj).map(async ([k, v]) => [k, await this.recurseDecrypt(v, key)])
      );
      return this.objectFromEntries(entries as [string, any][]);
    }

    if (typeof obj === 'string') {
      try {
        const decrypted = await this.decryptValue(obj, key);
        return this.parsePrimitive(decrypted);
      } catch {
        return obj;
      }
    }

    return obj;
  }

  /**
   * Crea un objeto a partir de una lista de pares clave-valor.
   *
   * @param entries - Un arreglo de tuplas donde cada tupla contiene una clave (string) y un valor (any).
   * @returns Un objeto construido a partir de los pares clave-valor proporcionados.
   */
  objectFromEntries(entries: [string, any][]): { [key: string]: any } {
    return entries.reduce((acc, [k, v]) => {
      acc[k] = v;
      return acc;
    }, {} as any);
  }

  /**
   * Descifra una configuración cifrada utilizando una contraseña proporcionada.
   *
   * @param config Objeto de configuración cifrada que contiene los datos y la sal en base64url.
   * @param password Contraseña utilizada para derivar la clave de descifrado.
   * @returns Una promesa que resuelve con la configuración descifrada.
   * @throws Error si el formato de la configuración es inválido.
   */
  // async decryptConfig(config: EncryptedConfig, password: string): Promise<any> {
  //   const saltB64 = config._salt;
  //   const data = config.data;

  //   if (!saltB64 || !data) {
  //     throw new Error('Formato de config inválido');
  //   }

  //   const salt = this.base64urlToArrayBuffer(saltB64);
  //   const key = await this.deriveKey(password, salt);

  //   return this.recurseDecrypt(data, key);
  // }


  async decryptConfig(config: EncryptedConfig, password: string): Promise<any> {
    const saltB64 = config._salt;
    const data = config;
    // const data = config.data;

    // Si no está cifrado, simplemente devolver el objeto original
    if (!saltB64 || !data) {
      console.warn('Configuración no encriptada. Retornando sin modificar.');
      return config;
    }

    try {
      const salt = this.base64urlToArrayBuffer(saltB64);
      const key = await this.deriveKey(password, salt);
      return this.recurseDecrypt(data, key);
    } catch (error) {
      console.error('Error al desencriptar configuración:', error);
      throw error;
    }
  }


}

export interface EncryptedConfig {
  _salt: string;
  data: any;
}
